import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import axios from 'axios';
import { 
  Status,
  Header,
  HeaderTitle,
  ExpandIcon,
  MenuIcon,
  Body,
  DebriefState,
  DebriefStateItem,
  DebriefStateItemTitle,
  DebriefStateItemSubtitle,
  ContinueButton,
  Dropdown,
  DropdownItem,
  DropdownItemLabel,
  DropdownItemDots,
  DeleteMenu,
  DeleteMenuItem,
  DeleteIconContainer,
} from "./style.js";
import clsx from 'clsx';
import { LS_SELECTED_DEBRIEF_KEY } from '../../util';
import _ from "lodash";
import IconButton from '@material-ui/core/IconButton';
import DeleteDebriefDialog from '../DeleteDebriefDialog/index.jsx';
import TrashIcon from '../../../../../../components/icons/TrashIcon';
import { navActions } from '../../../../../../redux/nav';

const currentStage = (debrief) => {
  let last_completed = debrief.completed_stages.lastIndexOf(true);

  //no completed stages so current stage is the first one
  if (last_completed < 0) return 0;

  //no current stage if all stages are complete
  if (last_completed === 4) return 4;

  //curent stage is the next stage after last completed
  return last_completed + 1;
}

const debriefStatusSmall = (debrief) => {
  return debrief.completed_stages.map( (completed,index) => {
    let color='gray';
    if (currentStage(debrief) === index) color='orange';
    if (completed) color = 'blue';

    const classNames = `dot ${color}`
    return <DropdownItemDots key={index} className={classNames}></DropdownItemDots>;
  })
}

const debriefStatusLarge = (debrief) => {
  //debriefer status headings
  const statusData = [
    {title: 'Prepare to Debrief', subtitle: 'Facilitator & Manager'},
    {title: 'Record Notes', subtitle: 'Facilitator'},
    {title: 'Review & Reflect', subtitle: 'Manager'},
    {title: 'Develop Action Plan', subtitle: 'Manager & Staff'},
    {title: 'Take Action', subtitle: 'Manager & Staff'},
  ];

    //debriefer status as list elements
  const stage = currentStage(debrief);
  const statusResults = statusData.map( (data,index) => {
    let completed = Array.isArray(debrief.completed_stages) && debrief.completed_stages[index];
        //if the last stage is complete it cannot ever be active, it should show the blue icon instead
        let active = completed && index === 4 ? false : stage === index;
    return (
      <DebriefStateItem key={index} className={clsx('circle-icon', `icon-${index+1}`, completed && 'icon--check', active && 'icon--active')}>
        <DebriefStateItemTitle active={active}>{data.title}</DebriefStateItemTitle>
        <DebriefStateItemSubtitle>{data.subtitle}</DebriefStateItemSubtitle>
      </DebriefStateItem>
    )
  })

  //add seperators
  let results = [];
  for(var i=0;i<statusResults.length;i++){
    results.push(statusResults[i]);
    results.push(<DebriefStateItem className="seperator"></DebriefStateItem>);
  }
  results.pop(); //cleanup extra seperator at the end

  return results;
}

const getInitialLoadedDebrief = (debriefs) => {
  const saved_raw = localStorage.getItem(LS_SELECTED_DEBRIEF_KEY);

  //early return if there are no debriefs
  if (!Array.isArray(debriefs) || debriefs.length == 0) return null;

  //auto-select the first debrief
  const defaultDebrief = Array.isArray(debriefs) && debriefs.length > 0 ? debriefs[0] : null;
    
  //return default debrief if there is no saved value
  if (!saved_raw) return defaultDebrief;

  try {
    //try to parse
    const saved = JSON.parse(saved_raw);

    //ensure the saved debrief matches the list of debriefs we have (saved value could be from another unit) or just return the default
    return _.find(debriefs, d => d.id === saved.id) || defaultDebrief;
        
  }catch (ex) {
    //remove value if it can't be parsed
    localStorage.removeItem(LS_SELECTED_DEBRIEF_KEY);
    return defaultDebrief;
  }
}

function DebriefStatus({ user, debriefs, unit, summaryReport, submitting, getDebriefs, dispatch }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDebrief, setSelectedDebrief] = useState(getInitialLoadedDebrief(debriefs));
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    setSelectedDebrief(getInitialLoadedDebrief(debriefs));
  }, [JSON.stringify(debriefs)])

  const toggleMenu = () => {
    setDropdownOpen(!dropdownOpen);
  }

  const selectDebrief = id => {
    const previousDebrief = selectedDebrief;

    //list of debriefs that are in the menu to pick from
    const availableDebriefs = debriefs.filter( d => previousDebrief && d.id !== previousDebrief.id);

    //our selected debrief
    const newSelectedDebrief = availableDebriefs.length > 0 ? _.find(availableDebriefs, ['id', id]) : null;

    //save to local storage
    localStorage.setItem(LS_SELECTED_DEBRIEF_KEY, JSON.stringify({id: newSelectedDebrief.id}));

    setSelectedDebrief(newSelectedDebrief);
    setDropdownOpen(false);
  }

  const continueDebrief = () => {
    setContinuing(true);
    const urls = ['prepare','record','review','plan','action'];
    window.location = `${user.debriefServerUrl}/api/redirect/connect?unit=${encodeURIComponent(unit)}&return=${window.location.href}&name=${summaryReport.datasetName}&dataset=${summaryReport.datasetId}&loc=${selectedDebrief.location_id}&debrief=${selectedDebrief.id}&stage=${urls[currentStage(selectedDebrief)]}`
  }

  const handleDeleteMenu = event => {
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  }
  
  const handleDeletePopup = event => {
    event.stopPropagation();
    setAnchorEl(null);
    setDeleteDialogOpen(!deleteDialogOpen);
  }
  
  const handleDeleteDebrief = event => {
    setAnchorEl(null);
    setDeleteDialogOpen(!deleteDialogOpen);
    axios
      .post(`/api/report/delete-debrief/${selectedDebrief.id}`)
      .then(res => {
        if (res.status === 200) {
          const newActiveDebriefs = debriefs.filter(debrief => debrief.id !== selectedDebrief.id);
          if (newActiveDebriefs && newActiveDebriefs.length) {
            newActiveDebriefs[0] && selectDebrief(newActiveDebriefs[0].id);
          }
          getDebriefs();
          dispatch(navActions.setSnackbarOptions({ message: 'Debrief successfully deleted' }))
        } else {
          dispatch(navActions.setSnackbarOptions({ message: 'There was an error deleting your debrief' }))
        }
      });
  }
  
  const availableDebriefs = debriefs.filter(debrief => debrief.id !== selectedDebrief.id);
  const hasAvailableDebriefs = availableDebriefs.length > 0;

  return (
    <Status>
      <Header className={clsx({available: hasAvailableDebriefs})} onClick={() => hasAvailableDebriefs && toggleMenu()}>
        <HeaderTitle>{selectedDebrief.meeting.shift}</HeaderTitle>
        {hasAvailableDebriefs && <ExpandIcon className={clsx({open: dropdownOpen})} />}
        <IconButton onClick={handleDeleteMenu} size="small">
          <MenuIcon />
        </IconButton>
        <DeleteMenu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleDeleteMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <DeleteMenuItem onClick={handleDeletePopup}>
            <DeleteIconContainer>
              <TrashIcon />
            </DeleteIconContainer>
            Delete Debrief
          </DeleteMenuItem>
        </DeleteMenu>
      </Header>
      <Body>
        <Dropdown className={clsx({open: dropdownOpen})}>
          {availableDebriefs.map(debrief => (
            <DropdownItem key={debrief.id} onClick={() => selectDebrief(debrief.id)}>
              <DropdownItemLabel>{debrief.meeting.shift}</DropdownItemLabel>
              {debriefStatusSmall(debrief)}
            </DropdownItem>
          ))}
        </Dropdown>
        <DebriefState>
          {debriefStatusLarge(selectedDebrief)}
        </DebriefState>
        <ContinueButton className={clsx({loading:continuing, hidden:submitting})} onClick={continueDebrief} disabled={submitting} variant="contained">
          Continue Debrief
        </ContinueButton>
      </Body>
      <DeleteDebriefDialog 
        open={deleteDialogOpen} 
        name={selectedDebrief.meeting.shift} 
        handleCancel={handleDeletePopup} 
        handleConfirm={handleDeleteDebrief} 
      />
    </Status>
  );        
}

DebriefStatus.propTypes = {
  /** Summary report object from Redux */
  summaryReport: PropTypes.object,
  /** User object from Redux */
  user: PropTypes.object,
  /** Each object contains data about a debrief */
  debriefs: PropTypes.arrayOf(PropTypes.object),
  /** Name of currently selected unit */
  unit: PropTypes.string,
  /** Determines if new debrief is being created */
  submitting: PropTypes.bool,
  /** Makes call to backend to get debriefs array from database */
  getDebriefs: PropTypes.func,
  /** Updates the Redux store */
  dispatch: PropTypes.func,
}

export default connect(store => {
  return {
    summaryReport: store.summaryReport.summaryReport,
    user: store.user,
  };
})(DebriefStatus);
  


// WEBPACK FOOTER //
// ./src/pages/SummaryReport/components/Debriefs/components/DebriefStatus/index.jsx